import { isAfter } from "date-fns";

import {
  getGroupIdFromAccessId,
  getStudentIdFromAccessId,
  isGroupAccessId,
  isStudentAccessId,
} from "../../shared/access-id";
import {
  Capability,
  ROLES_MAP,
  getComputedRole,
  hasOrgCapability,
  hasSectionCapability,
} from "../../shared/capabilities";
import { SectionUserActivityType } from "../../shared/types/attendance";
import { SectionRole } from "../../shared/types/roles";
import { DEFAULT_CODE_KERNEL_LANG } from "../../shared/worksheets/code-kernels";

import {
  activitySelectors,
  chatMessageSelectors,
  classUserSelectors,
  courseGroupingSelectors,
  courseGroupingUserSelectors,
  courseSelectors,
  courseUserSelectors,
  createAppSelector,
  pageAccessDataSelectors,
  pageGroupSelectors,
  pageGroupUserSelectors,
  pageSelectors,
  sectionSelectors,
  sectionUserSelectors,
  selectCurrentSectionId,
  selectCurrentUserId,
  termSelectors,
  topicSelectors,
  userSelectors,
} from "./base";
import { selectAdminAccessibleCourses } from "./course";
import { getCourseRole } from "./course-user";
import { selectCurrentSection } from "./current";
import { selectClassChatReadAtForCurrentSection } from "./local";
import { selectCurrentOrgRole, selectIsCurrentOrgUserDisabled } from "./org";
import { selectCurrentPage } from "./page";
import {
  selectCurrentUserComputedRole,
  selectCurrentUserOrgRole,
  selectCurrentUserRole,
  selectUserIdsWhoCanCreateChatMessage,
  selectUserIdsWhoCanParticipateInSection,
} from "./permissions";
import {
  selectEnabledSectionUsers,
  selectEnabledSectionUsersForCurrentSection,
} from "./section-user";
import { getSortedPages, sortByTermYearRegex, sortTopicActivities } from "./sorting";
import type {
  CourseGroupingWithCoursesType,
  CourseWithGroupingType,
  EnhancedUserWithSectionActivity,
  ObjectWithSections,
  PageWithTopicIdType,
  UserWithSectionActivity,
} from "./types";
import { SectionUserActivityStatus } from "./types";
import { selectAdminSectionId, selectIsStudentPreview } from "./view";

import {
  type ActivityType,
  type CourseType,
  type FullSectionType,
  InteractionMode,
  type PageAccessDataType,
  type PageCategory,
  type PageType,
  type TermType,
  type TopicType,
} from "components/server-types";
import {
  DEFAULT_ORDER_FN,
  ORDER_BY_CODE,
  ORDER_BY_FULL_TITLE,
  ORDER_BY_TITLE,
} from "mds/hooks/use-ordered";
import { RootState } from "store";
import { getCourseAndSectionDisplayTitle, getCourseCodeAndSectionTitle } from "utils/course";
import { isGroupPage, isIndividualPage, isStaticPage } from "utils/page-utils";
import { isLinkedSection, isTemplateSection, isUnlinkedSection } from "utils/publishing";
import { getEnabledSectionRole } from "utils/roles";
import {
  CONTENT_SIDEBAR_TAB_NAME,
  HOME_ROLE_TO_SECTION_ROLES,
  HomeRole,
  SectionPageCategoryToSidebarTab,
  pageUrl,
  sectionHomeUrl,
} from "utils/urls";
import { getUserFullName } from "utils/user-utils";

export const selectOrgIdForSectionId = (state: RootState, sectionId: string | null | undefined) =>
  sectionId && state.auth.section_to_org_id_map && sectionId in state.auth.section_to_org_id_map
    ? state.auth.section_to_org_id_map[sectionId]
    : null;

export const selectUserNameById = createAppSelector([userSelectors.selectById], (user) =>
  getUserFullName(user),
);

export const selectCurrentSectionKind = createAppSelector(
  [selectCurrentSection],
  (currentSection) => currentSection?.kind,
);

export const selectCurrentSectionIsLinked = createAppSelector(
  [selectCurrentSection],
  (currentSection) => isLinkedSection(currentSection),
);

export const selectCurrentSectionIsTemplate = createAppSelector(
  [selectCurrentSection],
  (currentSection) => isTemplateSection(currentSection),
);

export const selectCurrentSectionIsUnlinked = createAppSelector(
  [selectCurrentSection],
  (currentSection) => isUnlinkedSection(currentSection),
);

// Selects the admin section if set, otherwise the current section.
export const selectDesignatedSection = createAppSelector(
  [selectAdminSectionId, selectCurrentSectionId, sectionSelectors.selectEntities],
  (adminSectionId, currentSectionId, sections) => {
    const sectionId = adminSectionId ?? currentSectionId;
    return sectionId && sectionId in sections ? sections[sectionId] : null;
  },
);

export const selectOrgNameBySectionId = createAppSelector(
  [
    (state, sectionId: string) => state.auth.section_to_org_id_map?.[sectionId],
    (state) => state.orgs,
  ],
  (orgId, orgs) => (orgId ? orgs[orgId]?.name || "" : ""),
);

export const selectPageAccessDataForAccessId = createAppSelector(
  [
    sectionUserSelectors.selectAll,
    pageSelectors.selectById,
    pageAccessDataSelectors.filterByPageId,
    (state, pageId: string | null, selectedAccessId: string | null | undefined) => selectedAccessId,
  ],
  (sectionUsers, page, pageAccessData, selectedAccessId) => {
    if (!selectedAccessId || !page) {
      return null;
    }

    if (isGroupAccessId(selectedAccessId)) {
      const groupId = getGroupIdFromAccessId(selectedAccessId);
      return pageAccessData.find((pe) => pe.page_group_id === groupId);
    }

    if (isStudentAccessId(selectedAccessId)) {
      const studentId = getStudentIdFromAccessId(selectedAccessId);
      const sectionUser = sectionUsers.find(
        (cu) => cu.user_id === studentId && cu.section_id === page.section_id,
      );
      if (sectionUser) {
        return pageAccessData.find((pe) => pe.section_user_id === sectionUser.id);
      }
    }

    return pageAccessData?.[0];
  },
);

export const selectPageAccessDataForSectionUser = createAppSelector(
  [
    pageGroupSelectors.filterByPageId,
    pageGroupUserSelectors.selectAll,
    pageSelectors.selectById,
    pageAccessDataSelectors.filterByPageId,
    (state, pageId: string, sectionUserId: string | null) => sectionUserId,
  ],
  (
    pageGroups,
    pageGroupUsers,
    page,
    pageAccessData,
    sectionUserId,
  ): PageAccessDataType | undefined => {
    if (!sectionUserId) {
      return undefined;
    }

    if (isGroupPage(page)) {
      const pageGroupIds = pageGroupUsers
        .filter((pgu) => pgu.section_user_id === sectionUserId && pgu.is_ready)
        .map((pgu) => pgu.page_group_id);

      const groupId = pageGroups.find((pg) => pageGroupIds.includes(pg.id))?.id;

      return pageAccessData.find((data) => data.page_group_id === groupId);
    }

    if (isIndividualPage(page) || isStaticPage(page)) {
      // Static pages are treated as individual pages for the purpose of marking pages complete.
      return pageAccessData.find((data) => data.section_user_id === sectionUserId);
    }

    return undefined;
  },
);

export const selectEnabledSectionUsersByRole = createAppSelector(
  [selectEnabledSectionUsers, (state, role?: SectionRole) => role],
  (sectionUsers, role) => sectionUsers.filter((cu) => !role || cu.role === role),
);

export const selectCurrentSectionKernelLang = (state: RootState) =>
  selectCurrentSection(state)?.code_blocks_default_language || DEFAULT_CODE_KERNEL_LANG;

export const selectFullSectionsSorted = createAppSelector(
  [sectionSelectors.selectAll, (state) => state.courses],
  (sections, courses): FullSectionType[] =>
    sections
      .map((section) => {
        const course = courses[section.course_id];
        // It's possible during the initial rendering of a page that we haven't loaded
        // the Courses yet, so bail out if we can't find one. The selector will refresh
        // when the Courses are eventually loaded.
        if (!course) return null;
        return {
          ...section,
          full_title: getCourseAndSectionDisplayTitle(course, section),
          course_code_section_title: getCourseCodeAndSectionTitle(course, section),
        };
      })
      .filter((section) => section != null)
      .sort(ORDER_BY_FULL_TITLE),
);

export const selectSectionsForCurrentUser = createAppSelector(
  [
    (state, role: HomeRole | null | undefined) => role,
    sectionSelectors.selectAll,
    sectionUserSelectors.filterForCurrentUser,
  ],
  (role, sections, sectionUsers) =>
    sections.filter(
      (section) =>
        role === HomeRole.ADMIN ||
        (!!role &&
          sectionUsers.some(
            (cu) => cu.section_id === section.id && cu.role === HOME_ROLE_TO_SECTION_ROLES[role],
          )),
    ),
);

export const selectTemplateSections = createAppSelector([sectionSelectors.selectAll], (sections) =>
  sections.filter((section) => isTemplateSection(section)),
);

export const selectLinkedSectionsForCurrentSection = createAppSelector(
  [selectCurrentSection, sectionSelectors.selectAll],
  (currentSection, sections) => {
    if (!currentSection || !isTemplateSection(currentSection)) return [];
    return sections
      .filter((s) => isLinkedSection(s) && s.template_id === currentSection.id)
      .sort(ORDER_BY_TITLE);
  },
);

export const selectLinkedSectionCountForCurrentSection = createAppSelector(
  [selectLinkedSectionsForCurrentSection],
  (linkedSections) => linkedSections.length,
);

export const selectCurrentCourseCode = createAppSelector(
  [selectCurrentSection, (state) => state.courses],
  (section, courses) => (section?.course_id && courses[section.course_id]?.code) || null,
);

export const selectCurrentCourseTitle = createAppSelector(
  [selectCurrentSection, (state) => state.courses],
  (section, courses) => (section?.course_id && courses[section.course_id]?.title) || null,
);

export const selectCurrentCourseId = createAppSelector(
  [selectCurrentSection],
  (section) => section?.course_id || null,
);

export const selectCourseCodeBySectionId = createAppSelector(
  [sectionSelectors.selectById, (state) => state.courses],
  (section, courses) => (section?.course_id && courses[section.course_id]?.code) || null,
);

export const selectLmsSectionId = createAppSelector(
  [selectCurrentSection],
  (currentSection) => currentSection?.lms_section_id || null,
);

export const selectCurrentSectionTitle = createAppSelector(
  [selectCurrentSection],
  (currentSection) => currentSection?.title || null,
);

export const selectSectionTitleBySectionId = createAppSelector(
  [sectionSelectors.selectById],
  (section) => section?.title || null,
);

export const selectCurrentSectionDescription = createAppSelector(
  [selectCurrentSection],
  (currentSection) => currentSection?.description || null,
);

export const selectCurrentSectionAvEnabled = createAppSelector(
  [selectCurrentSection],
  (currentSection) => currentSection?.av_enabled || false,
);

// TODO: Put these two in a more appropriate place.
export const determineActivityStatus = (
  activity: SectionUserActivityType | null,
): SectionUserActivityStatus => {
  if (!activity) {
    return SectionUserActivityStatus.OFFLINE;
  }

  return activity.active === "0"
    ? SectionUserActivityStatus.INACTIVE
    : SectionUserActivityStatus.ACTIVE;
};

const determineCurrentPageId = (activity: SectionUserActivityType | null): string | null => {
  if (!activity) {
    return null;
  }

  return activity.pageId;
};

export const selectActivityStatus = (state: RootState) => state.attendance.sectionUserActivities;

export const selectActivityDataForUser = createAppSelector(
  [selectActivityStatus, (state, userId: string | null | undefined) => userId],
  (sectionUserActivities, userId) =>
    userId && userId in sectionUserActivities ? sectionUserActivities[userId] : null,
);

export const selectActivityStatusForUser = createAppSelector(
  [selectActivityDataForUser],
  (activityData) => determineActivityStatus(activityData),
);

export const selectFollowingStatusForUser = createAppSelector(
  [selectActivityDataForUser],
  (activityData) => activityData?.following === "1" || false,
);

export const selectActivePageIdForUser = createAppSelector(
  [selectActivityDataForUser],
  (activityData) => determineCurrentPageId(activityData),
);

export const selectActivityDataForCurrentUser = createAppSelector(
  [selectCurrentSectionId, selectActivityStatus],
  (sectionId, sectionUserActivities) => {
    return sectionId && sectionId in sectionUserActivities
      ? sectionUserActivities[sectionId] || null
      : null;
  },
);

export const selectActivePageIdForCurrentUser = createAppSelector(
  [selectActivityDataForCurrentUser],
  (activityData) => determineCurrentPageId(activityData),
);

export const selectUsersWithSectionActivityInCurrentSection = createAppSelector(
  [
    sectionUserSelectors.filterForCurrentSection,
    userSelectors.selectEntities,
    (state) => state.attendance.sectionUserActivities,
  ],
  (sectionUsers, users, sectionUserActivities): UserWithSectionActivity[] =>
    sectionUsers.map((sectionUser) => {
      const user = users[sectionUser.user_id];
      return {
        ...user,
        section_user_id: sectionUser.id,
        sectionActivityStatus: determineActivityStatus(sectionUserActivities[sectionUser.user_id]),
      };
    }),
);

export const selectStudentsWithSectionActivityInCurrentSection = createAppSelector(
  [selectUsersWithSectionActivityInCurrentSection, selectUserIdsWhoCanParticipateInSection],
  (users, studentUserIds) => users.filter((user) => studentUserIds.includes(user.id)),
);

export const selectUserIdsInCurrentSection = createAppSelector(
  [selectEnabledSectionUsersForCurrentSection],
  (sectionUsers) => sectionUsers.map((cu) => cu.user_id),
);

export const selectClassUserByUserIdMap = createAppSelector(
  [classUserSelectors.filterForCurrentSection],
  (classUsers) => new Map(classUsers.map((classUser) => [classUser.user_id, classUser])),
);

// NOTE: this is used in the SectionUsersSidebarPanel where we need to sort a list of users by
// section activity status and by specific fields on the ClassUser model. This could be refactored
// in the future to be more elegant/efficient, but it works for now.
export const selectEnhancedUsersWithSectionActivityInCurrentSection = createAppSelector(
  [
    sectionUserSelectors.filterForCurrentSection,
    selectClassUserByUserIdMap,
    userSelectors.selectEntities,
    (state) => state.attendance.sectionUserActivities,
  ],
  (sectionUsers, classUserMap, users, sectionUserActivities): EnhancedUserWithSectionActivity[] =>
    sectionUsers.map((sectionUser) => ({
      ...users[sectionUser.user_id],
      sectionActivityStatus: determineActivityStatus(sectionUserActivities[sectionUser.user_id]),
      isFollowing: sectionUserActivities[sectionUser.user_id]?.following === "1" || false,
      hand_raised_at: classUserMap.get(sectionUser.user_id)?.hand_raised_at || null,
      is_in_class_session: classUserMap.get(sectionUser.user_id)?.is_in_class_session || false,
    })),
);

export const selectUserCountInCurrentSection = createAppSelector(
  [selectEnabledSectionUsersForCurrentSection],
  (sectionUsers) => sectionUsers.length,
);

export const selectOnlineUserCountInCurrentSection = createAppSelector(
  [selectUsersWithSectionActivityInCurrentSection],
  (users) =>
    users?.filter((user) => user.sectionActivityStatus !== SectionUserActivityStatus.OFFLINE)
      .length || 0,
);

export const selectSortedTermSectionsByCourse = createAppSelector(
  [(state, courseId: string) => courseId, termSelectors.selectAll, sectionSelectors.selectAll],
  (courseId, terms, sections) => {
    const filteredSections = sections.filter((section) => section.course_id === courseId);

    const termsWithSections = terms.map<ObjectWithSections<TermType>>((term) => {
      const itemSections = filteredSections.filter((section) => section.term_id === term.id);
      const sortedSections = itemSections.toSorted(ORDER_BY_TITLE);
      const sortedSectionsWithOrder = sortedSections.map((section, index) => ({
        ...section,
        order: index,
      }));
      return { ...term, sections: sortedSectionsWithOrder };
    });

    return termsWithSections.sort(sortByTermYearRegex);
  },
);

export const selectCurrentTerm = createAppSelector(
  [selectCurrentSection, (state) => state.terms],
  (currentSection, terms) => (currentSection?.term_id && terms[currentSection?.term_id]) || null,
);

export const selectCurrentTermId = createAppSelector(
  [selectCurrentSection],
  (section) => section?.term_id || null,
);

export const selectCurrentTermTitle = createAppSelector(
  [selectCurrentTerm],
  (currentTerm) => currentTerm?.title || null,
);

export const selectSortedCoursesWithSectionsByTerm = createAppSelector(
  [(state, termId: string) => termId, selectAdminAccessibleCourses, sectionSelectors.selectAll],
  (termId, courses, sections): ObjectWithSections<CourseType>[] => {
    const filteredSections = sections.filter((section) => section.term_id === termId);

    const coursesWithSections = courses.map<ObjectWithSections<CourseType>>((course) => {
      const itemSections = filteredSections.filter((section) => section.course_id === course.id);
      const sortedSections = itemSections.toSorted(ORDER_BY_TITLE);
      const sortedSectionsWithOrder = sortedSections.map((section, index) => ({
        ...section,
        order: index,
      }));
      return { ...course, sections: sortedSectionsWithOrder };
    });

    return coursesWithSections.sort(ORDER_BY_CODE);
  },
);

export type GroupingWithCoursesAndSectionsType = {
  id: string | null;
  title: string;
  courses: ObjectWithSections<CourseType>[];
};

/**
 * Returns groupings with their courses and sections for a given term.
 * Includes a "fake" grouping (id: null) for ungrouped courses.
 * All groupings, courses, and sections are sorted by title.
 */
export const selectGroupingsWithCoursesByTerm = createAppSelector(
  [
    (state, termId: string) => selectSortedCoursesWithSectionsByTerm(state, termId),
    (state) => state.course_groupings,
  ],
  (coursesWithSections, groupings): GroupingWithCoursesAndSectionsType[] => {
    // Group courses by grouping_id
    const groupedCourses: Record<string, ObjectWithSections<CourseType>[]> = {};
    const ungroupedCourses: ObjectWithSections<CourseType>[] = [];

    coursesWithSections.forEach((course) => {
      if (course.sections.length === 0) {
        return;
      }

      if (course.grouping_id) {
        if (!groupedCourses[course.grouping_id]) {
          groupedCourses[course.grouping_id] = [];
        }

        groupedCourses[course.grouping_id].push(course);
      } else {
        ungroupedCourses.push(course);
      }
    });

    // Build result with real groupings
    const result: GroupingWithCoursesAndSectionsType[] = Object.entries(groupedCourses).map(
      ([id, courses]) => ({
        id,
        title: groupings[id]?.title || "",
        courses: courses.sort(ORDER_BY_CODE),
      }),
    );

    // Add fake grouping for ungrouped courses
    if (ungroupedCourses.length > 0) {
      result.push({
        id: null,
        title: "",
        courses: ungroupedCourses.sort(ORDER_BY_CODE),
      });
    }

    // Sort groupings by title, but keep uncategorized (null id) at the end
    return result.sort((a, b) => {
      if (a.id === null) return 1;
      if (b.id === null) return -1;

      // eslint-disable-next-line babel/new-cap
      return ORDER_BY_TITLE(a, b);
    });
  },
);

export const selectCourseSectionsByTerms = createAppSelector(
  [termSelectors.selectAll, courseSelectors.selectAll, sectionSelectors.selectAll],
  (terms, courses, sections) => {
    return terms.reduce(
      (acc, term) => {
        const filteredSections = sections.filter((section) => section.term_id === term.id);
        const coursesWithSections = courses.map<ObjectWithSections<CourseType>>((course) => {
          const itemSections = filteredSections
            .filter((section) => section.course_id === course.id)
            .map((section, index) => ({ ...section, order: index }));
          return { ...course, sections: itemSections };
        });
        // eslint-disable-next-line no-param-reassign
        acc[term.id] = coursesWithSections;
        return acc;
      },
      {} as Record<string, ObjectWithSections<CourseType>[]>,
    );
  },
);

export const selectCourseGroupingsWithCourses = createAppSelector(
  [courseGroupingSelectors.selectAll, selectAdminAccessibleCourses, selectCurrentUserOrgRole],
  (groupings, courses, orgRole) => {
    // Create a map of grouping_id to courses
    const coursesByGrouping = new Map<string, CourseType[]>();
    courses.forEach((course) => {
      if (course.grouping_id) {
        const existing = coursesByGrouping.get(course.grouping_id) || [];
        coursesByGrouping.set(course.grouping_id, [...existing, course]);
      }
    });

    const showEmptyCourses = hasOrgCapability(orgRole, Capability.CAN_AUTHOR_ORG);
    return groupings
      .flatMap<CourseGroupingWithCoursesType>((grouping) => {
        const groupingCourses = coursesByGrouping.get(grouping.id) ?? [];
        if (!showEmptyCourses && groupingCourses.length === 0) {
          return [];
        }

        return [{ ...grouping, courses: groupingCourses.sort(ORDER_BY_CODE) }];
      })
      .sort(ORDER_BY_TITLE);
  },
);

export const selectUngroupedCourses = createAppSelector([selectAdminAccessibleCourses], (courses) =>
  courses.filter((course) => !course.grouping_id).sort(ORDER_BY_CODE),
);

export const selectCoursesByGroupingId = createAppSelector(
  [(state, groupingId: string | null) => groupingId, courseSelectors.selectAll],
  (groupingId, courses) =>
    courses.filter((course) => course.grouping_id === groupingId).sort(ORDER_BY_CODE),
);

export const selectCoursesWithGrouping = createAppSelector(
  [courseSelectors.selectAll, (state) => state.course_groupings],
  (courses, groupings): CourseWithGroupingType[] => {
    // Create a map of grouping_id to grouping title for quick lookup
    return courses.map((course) => ({
      ...course,
      groupingTitle:
        course.grouping_id && course.grouping_id in groupings
          ? groupings[course.grouping_id].title
          : null,
    }));
  },
);

export const selectAuthorizedPages = createAppSelector(
  [
    pageSelectors.selectAll,
    selectCurrentUserComputedRole,
    selectEnabledSectionUsers,
    selectCurrentUserId,
    selectCurrentSectionId,
    (_state, homeRole?: HomeRole | null) => homeRole,
  ],
  (pages, currentComputedRole, sectionUsers, userId, currentSectionId, homeRole) => {
    // Admin / unset → current section only (ADMIN maps to null in HOME_ROLE_TO_SECTION_ROLES).
    const sectionRole = homeRole != null ? HOME_ROLE_TO_SECTION_ROLES[homeRole] : null;
    const sectionIds = sectionRole
      ? sectionUsers
          .filter((cu) => cu.user_id === userId && cu.role === sectionRole)
          .map((cu) => cu.section_id)
      : [currentSectionId];

    const effectiveRole = sectionRole ? ROLES_MAP[sectionRole] : currentComputedRole;
    const canAuthorSection = hasSectionCapability(effectiveRole, Capability.CAN_AUTHOR_SECTION);
    return pages.filter(
      (page) => sectionIds.includes(page.section_id) && (canAuthorSection || page.released_at),
    );
  },
);

export const selectPagesForCurrentSection = createAppSelector(
  [(state, category?: PageCategory) => category, (state) => selectAuthorizedPages(state)],
  (category, pages): PageType[] => pages.filter((p) => !category || p.category === category),
);

export const selectHasAccessablePages = createAppSelector([selectPagesForCurrentSection], (pages) =>
  pages.some((page) => page.interaction_mode !== InteractionMode.STATIC),
);

export const selectGroupPagesForCurrentSection = createAppSelector(
  [selectPagesForCurrentSection, activitySelectors.selectAll, topicSelectors.selectAll],
  (pages, activities, topics) => {
    // Filter out individual pages and only keep group pages.
    const groupPages = pages.filter((p) => isGroupPage(p));

    const topicById = topics.reduce(
      (acc, topic) => {
        return { [topic.id]: topic, ...acc };
      },
      {} as Record<string, TopicType>,
    );

    const activityById = activities.reduce(
      (acc, activity) => {
        return { [activity.id]: activity, ...acc };
      },
      {} as Record<string, ActivityType>,
    );

    return groupPages.map((page) => {
      if (page.activity_id) {
        return {
          ...page,
          topic_id: topicById[activityById[page.activity_id]?.topic_id]?.id || undefined,
        } as PageWithTopicIdType;
      }

      return page as PageWithTopicIdType;
    });
  },
);

export const selectSectionsByTermAndCourse = createAppSelector(
  [
    (state, termId: string | null) => termId,
    (state, termId: string | null, courseId: string | null) => courseId,
    sectionSelectors.selectAll,
  ],
  (termId, courseId, sections) =>
    sections
      .filter(
        (section) =>
          (!termId || section.term_id === termId) && (!courseId || section.course_id === courseId),
      )
      .sort(ORDER_BY_TITLE),
);

export type StudentAccessIdInfoType = {
  name: string;
  accessId: string;
};

/**
 * Sections the current user can author, resolved per-section via getComputedRole +
 * CAN_AUTHOR_SECTION (scoped-first precedence including User.role).
 */
export const selectSectionsThatCanBeAuthored = createAppSelector(
  [
    selectIsStudentPreview,
    selectFullSectionsSorted,
    sectionUserSelectors.filterForCurrentUser,
    courseUserSelectors.filterForCurrentUser,
    courseGroupingUserSelectors.filterForCurrentUser,
    (state) => state.courses,
    selectCurrentOrgRole,
    selectIsCurrentOrgUserDisabled,
    selectCurrentUserRole,
  ],
  (
    isStudentPreview,
    sections,
    sectionUsers,
    courseUsers,
    groupingUsers,
    courses,
    orgRole,
    isOrgUserDisabled,
    userRole,
  ) => {
    if (isStudentPreview) {
      return [];
    }

    return sections.filter((section) => {
      const sectionUser = sectionUsers.find((su) => su.section_id === section.id) ?? null;
      const sectionRole = getEnabledSectionRole(sectionUser, isOrgUserDisabled);
      const course = courses[section.course_id] ?? null;
      const courseRole = getCourseRole(courseUsers, groupingUsers, course, isOrgUserDisabled);
      const computedRole = getComputedRole({
        userRole,
        sectionRole,
        orgRole,
        courseRole,
      });
      return hasSectionCapability(computedRole, Capability.CAN_AUTHOR_SECTION);
    });
  },
);

// When deleting the page the user is currently on, we need to redirect to another page.
// This selector provides the URL to redirect to.
// - If the page we are deleting is part of a topic (whether it has a due date or not), we redirect to the previous
//   page in the same topic. If the page was the first page of the topic, we redirect to the next one within the same
//   topic. We try to stay within the same activity/subcategory (prep work, additional resources, etc.) but if
//   there are no other pages in that category, we will redirect to the previous or next page within the same topic.
// - If the page is an assignment (but not part of a topic), course resource or private workspace page, we redirect to
//   the previous page within the same section. If the page was the first page of the section, we redirect to the next
//   one within the same section.
// - If there are no page left within that topic or section, we redirect to the section page.
export const selectRedirectUrlForPageDeletion = createAppSelector(
  [
    (state, targetPageId: string | undefined | null) => targetPageId,
    selectCurrentSectionId,
    selectCurrentUserComputedRole,
    selectCurrentPage,
    activitySelectors.selectAll,
    pageSelectors.filterForCurrentSection,
  ],
  (targetPageId, currentSectionId, computedRole, page, activities, pages) => {
    const canAuthorSection = hasSectionCapability(computedRole, Capability.CAN_AUTHOR_SECTION);
    if (!canAuthorSection || !currentSectionId || !page || page.id !== targetPageId) {
      return null;
    }
    const SECTION_OVERVIEW_URL = sectionHomeUrl(currentSectionId);
    let sortedPages: PageType[] = [];
    if (page.category === "activity_page") {
      const activity = activities.find((a) => a.id === page.activity_id);
      const topicId = activity?.topic_id;
      if (!topicId) {
        return SECTION_OVERVIEW_URL;
      }
      const sortedActivities = sortTopicActivities(topicId, activities, pages, canAuthorSection);
      sortedPages = getSortedPages(sortedActivities);
    } else {
      sortedPages = pages.filter((p) => p.category === page.category).sort(DEFAULT_ORDER_FN);
    }
    const currentPageIndex = sortedPages.findIndex((p) => p.id === page.id);
    if (currentPageIndex === -1) {
      return SECTION_OVERVIEW_URL;
    }
    // If at all possible, we try to stay within the same activity/subcategory
    const previousPage = sortedPages[currentPageIndex - 1];
    const nextPage = sortedPages[currentPageIndex + 1];
    // sortedPages is either empty or only contains the current page.
    if (!previousPage && !nextPage) {
      return SECTION_OVERVIEW_URL;
    }
    // For the first page of sortedPages, we always redirect to the next page.
    if (!previousPage) {
      const tab = SectionPageCategoryToSidebarTab[nextPage.category] as CONTENT_SIDEBAR_TAB_NAME;
      return pageUrl(currentSectionId, nextPage.id, { tab });
    }
    // For the last page of sortedPages, we always redirect to the previous page.
    if (!nextPage) {
      const tab = SectionPageCategoryToSidebarTab[
        previousPage.category
      ] as CONTENT_SIDEBAR_TAB_NAME;
      return pageUrl(currentSectionId, previousPage.id, { tab });
    }
    // If the previous page is part of the same activity/subcategory, we redirect to it.
    if (previousPage.activity_id === page.activity_id) {
      const tab = SectionPageCategoryToSidebarTab[
        previousPage.category
      ] as CONTENT_SIDEBAR_TAB_NAME;
      return pageUrl(currentSectionId, previousPage.id, { tab });
    }
    // If the next page is part of the same activity/subcategory, we redirect to it.
    if (nextPage.activity_id === page.activity_id) {
      const tab = SectionPageCategoryToSidebarTab[nextPage.category] as CONTENT_SIDEBAR_TAB_NAME;
      return pageUrl(currentSectionId, nextPage.id, { tab });
    }
    // If we can't stay within the same activity/subcategory, we redirect to the previous page.
    const tab = SectionPageCategoryToSidebarTab[previousPage.category] as CONTENT_SIDEBAR_TAB_NAME;
    return pageUrl(currentSectionId, previousPage.id, { tab });
  },
);

export const selectSortedChatMessagesForCurrentSection = createAppSelector(
  [chatMessageSelectors.filterForCurrentSection, selectUserIdsWhoCanCreateChatMessage],
  (chatMessages, userIdsWhoCanCreateChatMessage) =>
    chatMessages.filter((message) => userIdsWhoCanCreateChatMessage.includes(message.author_id)),
);

export const selectUnreadChatMessageCountForCurrentSection = createAppSelector(
  [selectSortedChatMessagesForCurrentSection, selectClassChatReadAtForCurrentSection],
  (chatMessages, readAt) => {
    if (!readAt) {
      return chatMessages.length;
    }
    return chatMessages.filter((message) => isAfter(message.created_at, readAt)).length;
  },
);
